


















































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Filter } from '@/models/reports/filter';
import { ReportApi } from '@/api/report-api';
import { namespace, Getter } from 'vuex-class';
import { Operator } from '@/models/reports/report-operators';
import { SelectItem } from '@/models/reports/select-item';
import { FieldSelectorSelection } from '@/models/field-selector/field-selector-selection';
import FieldSelector from '@/components/field-selector/field-selector.vue';
import { dateParse } from '@/date-picker-parser';
import ReportEntitySearchResultTemplate from '@/components/reports/report-entity-search-result-template.vue';
import { DateTime } from 'luxon';

const ReportModule = namespace('report');
const reportApi = new ReportApi();

@Component({
    components: {
        FieldSelector,
    },
})
export default class ReportFilter extends Vue {
    @Prop({ required: true }) public filter!: Filter;
    @Prop({ required: true }) public index!: number;
    @ReportModule.Getter public allReportOperators!: Array<Operator>;
    @ReportModule.Getter public allReportLogicalOperators!: Array<SelectItem>;
    @ReportModule.Getter public allReportBooleanValues!: Array<SelectItem>;
    private gettingForeignKeyValues: boolean = false;
    public parseDate = dateParse;
    private initalised: boolean = false;
    private tempDate: string | null = null;

    private selectedSelectItem: SelectItem | null = null;

    @Watch('selectedSelectItem')
    private selectedSelectItemChanged(): void {
        if (this.selectedSelectItem != null) {
            this.tempFilter.value = this.selectedSelectItem.value;
        }
    }

    private mounted() {
        if (this.hasDateValue && this.tempFilter.value != null) {
            this.tempFilter.value = DateTime.fromFormat(this.tempFilter.value!, 'dd/MM/yyyy hh:mm:ss').toFormat('dd/MM/yyyy');
            this.tempDate = this.tempFilter.value;
        }
    }

    @Watch('tempDate')
    private tempDateChanged(tempDate: string) {
        this.tempFilter.value = tempDate;
    }

    private entities: Array<SelectItem> = new Array<SelectItem>();
    private entitySearchTemplate = ReportEntitySearchResultTemplate;

    public lookupValues: Array<number>;

    // Not shallow copying. this filter's parents needs to know about changes. this component also needs to know about changes which would require two watches that cause an endless loop
    public tempFilter: Filter = this.filter;

    private get isValid(): boolean {
        if (this.tempFilter.field == null) {
            return false;
        }

        if (this.tempFilter.value == null) {
            return false;
        }

        if (this.tempFilter.operator == null) {
            return false;
        }
        return true;
    }

    @Watch('isValid')
    private errorsChanged(isValid: boolean) {
        this.$emit('valid-changed', isValid);
    }

    @Watch('filter')
    private filterChange(filter: Filter) {
        this.tempFilter = filter;
    }

    private get operators(): Array<SelectItem> {
        if (this.tempFilter.field == null) {
            this.tempFilter.operator = null;
            return new Array<SelectItem>();
        }

        const dataType = this.tempFilter.field.isForeignKey
            ? 'Lookup' // NOTE: use the Lookup operators for foreign keys
            : this.tempFilter.field.dataType;

        const operator = this.allReportOperators.find((x) => x.type === dataType);

        if (operator == null) {
            this.tempFilter.operator = null;
            return new Array<SelectItem>();
        }

        return operator.options;
    }

    private toggleVariable(): void {
        this.tempFilter.isVariable = !this.tempFilter.isVariable;
    }

    private remove() {
        this.$emit('remove-filter', this.tempFilter);
    }

    get showOperators(): boolean {
        if (this.operators == null) {
            return false;
        }
        return this.operators.length > 0;
    }

    get hasLookupValue(): boolean {
        if (!this.tempFilter.field) {
            return false;
        }
        return this.tempFilter.field.dataType === 'Lookup';
    }

    get hasMultiLookupValue(): boolean {
        if (!this.tempFilter.field) {
            return false;
        }
        return this.tempFilter.field.dataType === 'MultiValueLookup';
    }

    get hasBooleanValue(): boolean {
        if (!this.tempFilter.field) {
            return false;
        }

        return this.tempFilter.field.dataType === 'TrueFalse';
    }

    get hasDateValue(): boolean {
        if (!this.tempFilter.field) {
            return false;
        }

        return this.tempFilter.field.dataType === 'DateTime' ||
        this.tempFilter.field.dataType === 'Date' ||
        this.tempFilter.field.dataType === 'SmallDateTime' ||
        this.tempFilter.field.dataType === 'DateTimeOffset';
    }

    get hasForeignKeyValue(): boolean {
        if (!this.tempFilter.field) {
            return false;
        }

        return this.tempFilter.field.isForeignKey;
    }

    get hasDefaultValue(): boolean {
        return !this.hasLookupValue &&
        !this.hasDateValue &&
        !this.hasBooleanValue &&
        !this.hasMultiLookupValue &&
        !this.hasForeignKeyValue;
    }

    private resetEntities(): void {
        this.entities = new Array<SelectItem>();
        this.tempFilter.value = '';
    }

    public async fetchEntitesAsync(searchTerm: string): Promise<void> {
        if (this.gettingForeignKeyValues) {
            return;
        }
        this.gettingForeignKeyValues = true;

        try {
            const entities = await reportApi.fetchEntitiesBySearchTerm(searchTerm, this.tempFilter.field!.foreignKeyTable);
            this.entities = entities;
        } catch (e) {
            // should probably emit an error here
            this.gettingForeignKeyValues = false;
        }
        this.gettingForeignKeyValues = false;
    }

    private openFieldSelector(): void {
        this.$emit('open-field-selector', this.tempFilter);
    }
    public get showValue(): boolean {
        if (this.tempFilter.field == null) {
            return false;
        }

        if (this.tempFilter.isVariable) {
            return false;
        }
        if (this.tempFilter.operator == null) {
            return false;
        }

        return this.tempFilter.operator !== '0' && // IsNull
            this.tempFilter.operator !== '1' && // EmptyOrNull
            this.tempFilter.operator !== '12' && // LastDay
            this.tempFilter.operator !== '13' && // LastWeek
            this.tempFilter.operator !== '14' && // LastMonth
            this.tempFilter.operator !== '15' && // LastYear
            this.tempFilter.operator !== '16' && // NextDay
            this.tempFilter.operator !== '17' && // NextWeek
            this.tempFilter.operator !== '18' && // NextMonth
            this.tempFilter.operator !== '19' && // NextYear
            this.tempFilter.operator !== '25' && // IsNotNull
            this.tempFilter.operator !== '27'; // IsNotEmptyOrNull
    }

    public getLabel(selectItem: SelectItem | null): string {
        if (selectItem != null) {
            return selectItem.text;
        }

        return '';
    }
}
